import { useId, useState } from 'react';
import { Alert, Button, cn, Field, Icon, NotProvided } from '@evogard/ui';
import { formatPhone, normalizePhone, normalizePlate } from '@evogard/domain';
import { useDebouncedTerm } from '../../../lib/debounce.js';
import { ThirdPartyForm } from './ThirdPartyForm.js';
import { VehicleForm } from './VehicleForm.js';
import {
  MIN_SEARCH,
  useThirdParty,
  useThirdPartyPermissions,
  useThirdPartySearch,
  type ThirdPartyWithVehicles,
} from './useThirdParties.js';

/** O que o campo escolhe: a pessoa e, dela, um veículo. */
export interface ThirdPartyVehicleValue {
  thirdPartyId: number | null;
  thirdPartyVehicleId: number | null;
}

export const EMPTY_THIRD_PARTY_VEHICLE: ThirdPartyVehicleValue = {
  thirdPartyId: null,
  thirdPartyVehicleId: null,
};

/**
 * O terceiro envolvido e o veículo dele — um campo, com a lógica dentro.
 *
 * É controlado como um `Field`: recebe `value` e devolve pelo `onChange` o par
 * `{ thirdPartyId, thirdPartyVehicleId }` — exatamente o que o sinistro do
 * terceiro precisa (`thirdPartyVehicleId`, `claims.third_party_vehicle_id`).
 * Quem o usa não chama a API de terceiro: busca, cadastro da pessoa e do
 * veículo acontecem aqui dentro, por `useThirdParties`.
 *
 * ## O caminho, na ordem do plano (§3.2)
 *
 * 1. **Busca antes de cadastrar.** O terceiro não tem documento; sem procurar
 *    primeiro, a mesma pessoa vira duas linhas. A busca é um campo só — nome,
 *    telefone ou placa —, porque quem atende tem na mão o que a pessoa soube
 *    dizer.
 * 2. **Não achou → cadastra a pessoa**, com o que já foi digitado no nome.
 * 3. **Escolhe o veículo dela**, ou vincula um novo.
 *
 * ## Por que tudo acontece dentro do campo, sem modal
 *
 * "Diálogo modal é quase sempre preguiça" (`DESIGN.md`), e este campo vai
 * morar dentro do formulário do aviso, que já é um modal — modal dentro de
 * modal trava o foco duas vezes e esconde o formulário de onde se veio. Cada
 * passo se abre no lugar, e "Cancelar" volta ao passo anterior.
 *
 * ## Quem nomeia é quem está em volta
 *
 * O campo não desenha caixa nem legenda visível: ele sempre mora numa seção
 * ou num cartão que já diz o que ele é ("O terceiro e o carro dele" no
 * sinistro, "Novo terceiro" no cadastro). Com caixa e legenda próprias, a
 * mesma coisa aparecia com três nomes empilhados. A legenda continua, para
 * leitor de tela.
 *
 * A explicação de procurar antes de cadastrar também mora num lugar só: na
 * ajuda da busca, que é onde a pessoa está quando precisa dela.
 *
 * ## Sem veículo
 *
 * `withVehicle={false}` para no passo 1: o terceiro sem carro — o dono do
 * muro, do portão — é só a pessoa, e o campo devolve `thirdPartyVehicleId`
 * nulo. É o aviso do terceiro quem decide, pela resposta "Envolve veículo de
 * terceiro?" (`thirdPartyQuestions`).
 *
 * ## Permissão
 *
 * Quem só tem `claims.third_parties:ver` busca e escolhe, e não vê os botões
 * de cadastrar. Esconder não é proteger: a API cobra `criar` de novo.
 */
export function ThirdPartyVehicleField({
  label = 'Terceiro envolvido',
  value,
  onChange,
  required,
  withVehicle = true,
}: {
  label?: string;
  value: ThirdPartyVehicleValue;
  onChange: (value: ThirdPartyVehicleValue) => void;
  required?: boolean;
  /** `false`: só a pessoa, sem escolher carro (ver "Sem veículo"). */
  withVehicle?: boolean;
}) {
  const legendId = useId();

  return (
    <fieldset aria-labelledby={legendId} className="flex min-w-0 flex-col gap-3">
      <legend id={legendId} className="sr-only">
        {label}
        {required && (
          <>
            <span aria-hidden="true" className="ml-0.5 text-erro">
              *
            </span>
            <span className="sr-only"> (obrigatório)</span>
          </>
        )}
      </legend>

      {value.thirdPartyId === null ? (
        <PersonStep
          onPick={(thirdPartyId) => onChange({ thirdPartyId, thirdPartyVehicleId: null })}
        />
      ) : (
        <VehicleStep
          thirdPartyId={value.thirdPartyId}
          withVehicle={withVehicle}
          vehicleId={value.thirdPartyVehicleId}
          onPickVehicle={(thirdPartyVehicleId) =>
            onChange({ thirdPartyId: value.thirdPartyId, thirdPartyVehicleId })
          }
          onChangePerson={() => onChange(EMPTY_THIRD_PARTY_VEHICLE)}
        />
      )}
    </fieldset>
  );
}

/**
 * O que foi digitado na busca serve de nome para o cadastro?
 *
 * Só quando NÃO é um telefone nem uma placa — é isso que a busca também casa.
 * A primeira versão perguntava "tem algum dígito?", e o percurso no navegador
 * mostrou o defeito: um nome com número ("Maria 2", ou o nome de teste com o
 * instante) chegava vazio ao formulário.
 */
function looksLikeName(search: string): boolean {
  const term = search.trim();
  return (
    /\p{L}/u.test(term) && normalizePhone(term) === null && normalizePlate(term) === null
  );
}

/* ------------------------------------------------------------------------- *
 * Passo 1 — achar a pessoa, ou cadastrar
 * ------------------------------------------------------------------------- */

function PersonStep({ onPick }: { onPick: (thirdPartyId: number) => void }) {
  const { canCreate } = useThirdPartyPermissions();
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const term = useDebouncedTerm(search);
  const searching = term.trim().length >= MIN_SEARCH;
  const query = useThirdPartySearch(term, { perPage: 8, enabled: searching });

  if (creating) {
    return (
      <ThirdPartyForm
        initialName={looksLikeName(search) ? search.trim() : ''}
        onSaved={(created) => onPick(created.id)}
        onCancel={() => setCreating(false)}
      />
    );
  }

  const items = searching ? (query.data?.items ?? []) : [];

  return (
    <div className="flex flex-col gap-3">
      <Field
        label="Buscar terceiro"
        icon="busca"
        autoComplete="off"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        /* busca não envia nada: este campo mora dentro do formulário do sinistro,
           e o Enter aqui enviaria o sinistro inteiro pela metade */
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.preventDefault();
        }}
        placeholder="Nome, telefone ou placa"
        help={
          searching
            ? undefined
            : 'Procure antes de cadastrar — o terceiro não tem documento, e a mesma pessoa não pode virar duas.'
        }
      />

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}

      {searching && query.isPending && (
        <div aria-busy="true" className="flex flex-col gap-2">
          <div className="h-14 animate-pulse rounded-borda bg-superficie-suave" />
          <div className="h-14 animate-pulse rounded-borda bg-superficie-suave" />
        </div>
      )}

      {items.length > 0 && (
        <ul className="flex flex-col gap-2" aria-label="Terceiros encontrados">
          {items.map((t) => (
            <li key={t.id}>
              <PersonOption thirdParty={t} onPick={() => onPick(t.id)} />
            </li>
          ))}
        </ul>
      )}

      {searching && query.isSuccess && items.length === 0 && (
        <p className="text-sm text-texto-suave">
          Ninguém com &ldquo;{term.trim()}&rdquo; nesta base.
          {canCreate ? ' Se é a primeira vez desta pessoa, cadastre abaixo.' : ''}
        </p>
      )}

      {canCreate && (
        <div>
          <Button
            type="button"
            variant="contorno"
            icon="mais"
            onClick={() => setCreating(true)}
          >
            {searching && items.length === 0
              ? `Cadastrar "${search.trim()}"`
              : 'Cadastrar novo terceiro'}
          </Button>
        </div>
      )}
    </div>
  );
}

function PersonOption({
  thirdParty,
  onPick,
}: {
  thirdParty: ThirdPartyWithVehicles;
  onPick: () => void;
}) {
  const vehicles = thirdParty.vehicles;
  return (
    <button
      type="button"
      onClick={onPick}
      className={cn(
        'flex min-h-11 w-full items-center justify-between gap-3 rounded-borda border border-borda-forte px-3 py-2 text-left',
        'transition-[border-color,background-color] hover:border-texto-suave hover:bg-superficie-suave',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primaria-texto',
      )}
    >
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-sm font-medium text-texto">{thirdParty.name}</span>
        <span className="text-xs text-texto-suave">
          {formatPhone(thirdParty.phone)}
          {' · '}
          {vehicles.length === 0
            ? 'nenhum veículo'
            : vehicles.map((v) => v.plate).join(', ')}
        </span>
      </span>
      <Icon nome="seta-direita" size={16} className="shrink-0 text-texto-suave" />
    </button>
  );
}

/* ------------------------------------------------------------------------- *
 * Passo 2 — o veículo da pessoa escolhida
 * ------------------------------------------------------------------------- */

function VehicleStep({
  thirdPartyId,
  withVehicle,
  vehicleId,
  onPickVehicle,
  onChangePerson,
}: {
  thirdPartyId: number;
  withVehicle: boolean;
  vehicleId: number | null;
  onPickVehicle: (vehicleId: number | null) => void;
  onChangePerson: () => void;
}) {
  const { canCreate } = useThirdPartyPermissions();
  const query = useThirdParty(thirdPartyId);
  const [adding, setAdding] = useState(false);

  if (query.isError) {
    return (
      <div className="flex flex-col gap-3">
        <Alert tone="erro">{query.error.message}</Alert>
        <div>
          <Button type="button" variant="contorno" onClick={onChangePerson}>
            Escolher outro terceiro
          </Button>
        </div>
      </div>
    );
  }

  if (!query.data) {
    return (
      <div
        aria-busy="true"
        className="h-24 animate-pulse rounded-borda bg-superficie-suave"
      />
    );
  }

  const person = query.data;
  /* só os ativos se escolhem; o arquivado continua no sinistro que já o usa */
  const vehicles = person.vehicles.filter((v) => !v.archivedAt);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-medium text-texto">{person.name}</span>
          <span className="text-xs text-texto-suave">
            {formatPhone(person.phone)}
            {person.email ? ` · ${person.email}` : ''}
          </span>
        </div>
        <Button type="button" variant="discreto" size="sm" onClick={onChangePerson}>
          Trocar terceiro
        </Button>
      </div>

      {!withVehicle ? null : adding ? (
        <VehicleForm
          thirdPartyId={person.id}
          ownerName={person.name}
          onSaved={(vehicle) => {
            setAdding(false);
            onPickVehicle(vehicle.id);
          }}
          onCancel={() => setAdding(false)}
        />
      ) : (
        <>
          {vehicles.length === 0 ? (
            <p className="text-sm text-texto-suave">
              {person.name} ainda não tem veículo cadastrado.
              {canCreate ? ' Vincule o carro envolvido no sinistro.' : ''}
            </p>
          ) : (
            <div
              role="group"
              aria-label={`Veículos de ${person.name}`}
              className="flex flex-col gap-2"
            >
              {vehicles.map((v) => {
                const chosen = v.id === vehicleId;
                return (
                  <button
                    key={v.id}
                    type="button"
                    aria-pressed={chosen}
                    onClick={() => onPickVehicle(chosen ? null : v.id)}
                    className={cn(
                      'flex min-h-11 w-full items-center gap-3 rounded-borda border px-3 py-2 text-left',
                      'transition-[border-color,background-color]',
                      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primaria-texto',
                      chosen
                        ? 'border-primaria-texto bg-primaria-suave'
                        : 'border-borda-forte hover:border-texto-suave',
                    )}
                  >
                    <Icon
                      nome={chosen ? 'confirmado' : 'veiculo'}
                      size={16}
                      className={chosen ? 'text-texto' : 'text-texto-suave'}
                    />
                    <span className="flex flex-col">
                      <span className="text-sm font-medium text-texto tabular-nums">
                        {v.plate}
                      </span>
                      <span className="text-xs text-texto-suave">
                        {v.model || <NotProvided />} · {v.manufactureYear}/{v.modelYear}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {canCreate && (
            <div>
              <Button
                type="button"
                variant="contorno"
                icon="mais"
                onClick={() => setAdding(true)}
              >
                Vincular outro veículo
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
